<template>

  <wgu-module-card v-bind="$attrs" :moduleName="moduleName" class="wgu-insurewin" :icon="icon"
    v-on:visibility-change="show">

    <v-card-title primary-title>
      <!-- toggle button to choose measure type -->
      <ks-assess-type-chooser :assessType="assessType" :showAngleTool="showAngleTool" :iconsOnly="iconsOnly"
        @ks-assesstype-change="applyAssessType" />
    </v-card-title>
    <v-card-actions>
      <ks-assess-result :assessType="assessType" />
    </v-card-actions>
  </wgu-module-card>
</template>

<script>
import ModuleCard from './../modulecore/ModuleCard';
import { Mapable } from '../../mixins/Mapable';
import { WguEventBus } from '../../WguEventBus';
import AssessTypeChooser from './assessTypeChooser.vue';
import OlAssessController from './OlassessController';
import { TileWMS } from 'ol/source';
import axios from '@/axios';
import Option from '../../const/Option.js';
import { mapState } from 'vuex';
import { Tile as TileLayer } from 'ol/layer';
import assessResult from './assessResult.vue';

export default {
  name: 'ks-assessclick-win',
  inheritAttrs: false,
  components: {
    'wgu-module-card': ModuleCard,
    'ks-assess-type-chooser': AssessTypeChooser,
    'ks-assess-result': assessResult
  },
  mixins: [Mapable],
  props: {
    icon: { type: String, required: false, default: 'photo_size_select_small' },
    showAngleTool: { type: Boolean, required: false, default: false },
    iconsOnly: { type: Boolean, required: false, default: false }
  },
  data () {
    return {
      date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
      moduleName: 'ks-assessclick',
      measureGeom: null,
      assessType: 'emergenceSituation',
      dialog: false,
      dialogMsg: '',
      singleClickKey: null,
      insureLayer: null,
      selectedItem: {
        id: '',
        land_name: '',
        owner_name: '',
        address: '',
        area: '',
        crop_id: '',
        irrigation: '',
        seed_id: '',
        sowing_date: '',
        mature_days: '',
        fertilizer_id: '',
        pesticide_id: ''
      },
      trusteeLands: [],
      option: Option,
      assessLayer: null,
      prelAssessData: []
    }
  },
  async mounted () {
    await this.fetchAssessment();
  },
  beforeDestroy () {
    // 清理图层
    if (this.map && this.assessLayer) {
      this.map.removeLayer(this.assessLayer);
    }
  },
  destroy () {
    if (this.olMapCtrl) {
      this.olMapCtrl.destroy();
      this.olMapCtrl = undefined;
    }
  },
  watch: {
    assessType: {
      async handler () {
        // Update prelAssess data and layer when assessType changes
        await this.fetchAssessment();
      },
      immediate: true
    }
  },
  computed: {
    ...mapState(['selectedArea', 'selectedYear', 'selectedCrop'])
  },
  methods: {
    /**
       * (Un-)Register map interactions when the visibility of the module changes.
       *
       * @param  {boolean} visible New visibility state
       */
    show (visible) {
      if (!this.olMapCtrl) {
        return;
      }
      if (visible && this.measureType === 'area') {
        this.olMapCtrl.addInteraction(this.measureType, this.onMeasureVertexSet);
        if (this.singleClickKey) {
          this.map.un(this.singleClickKey.type, this.singleClickKey.listener);
        }
      } else {
        this.olMapCtrl.removeInteraction();
        this.onMapClickBound();
      }

      if (!visible) {
        if (this.map && this.assessLayer) {
          this.map.removeLayer(this.assessLayer);
        }
      } else {
        this.fetchAssessment();
      }
    },
    /**
       * Applies the changed assess value to this.assessType.
       * Called as callback of assessTypeChooser
       *
       * @param  {String} newAssessType New assess type
       * @param  {String} oldAssessType Old assess type
       */
    applyAssessType (newAssessType, oldAssessType) {
      this.assessType = newAssessType;
      console.log('applyAssessType', newAssessType, oldAssessType);
      this.fetchAssessment();
    },
    /**
    * This function is executed, after the map is bound (see mixins/Mapable)
    */
    onMapBound () {
      if (this.unbound) {
        return;
      }

      // TODO: Should we explicitly declare all attributes as props?
      this.olMapCtrl = new OlAssessController(this.map, this.$attrs);
      // Remove automatic creation here, let fetchAssessment handle it or unify logic
      // this.olMapCtrl.createAssessLayer();
    },
    /**
       * This function is executed, after the map is bound (see mixins/Mapable)
       */
    onMapUnbound () {
      if (this.olMapCtrl) {
        this.olMapCtrl.destroy();
        this.olMapCtrl = undefined;
      }
    },
    /**
     * This function is executed, after the map is bound (see mixins/Mapable)
     */
    onMapClickBound () {
      if (this.measureType === 'trustee') {
        // 防止多次绑定用户点击事件
        if (this.singleClickKey) {
          this.map.un(this.singleClickKey.type, this.singleClickKey.listener);
        }
      }
    },
    /**
     * Fetch Assess data from server
     */
    async fetchAssessment () {
      if (!this.map) {
        console.error('Map is not initialized');
        return;
      }

      try {
        const requestData = {
          area: this.selectedArea?.place_id || '', // Corrected property access
          year: this.selectedYear?.value || '2025', // Default if missing
          crop: this.selectedCrop?.value || 'corn',
          assessType: this.assessType || 'emergenceSituation'
        };

        const response = await axios.post('/assess/getAssessData', requestData);

        if (!response?.data) {
          throw new Error('Invalid response data');
        }

        if (response.data.success) {
          const data = response.data.data || {};
          this.prelAssessData = data.prelAssessData || [];
          const assessData = { assessData: data.assessData, assessType: requestData.assessType };
          // Emit the updated assess data with assessType
          WguEventBus.$emit('update-assess-data', assessData);

          if (!data.geoserver || !data.layer) {
            console.warn('Missing geoserver or layer information');
            return;
          }

          // Remove existing layer
          if (this.assessLayer) {
            this.map.removeLayer(this.assessLayer);
          }

          // Create new layer (using Proxy URL for consistency)
          const source = new TileWMS({
            url: '/geoserver/ks/wms',
            params: {
              LAYERS: data.layer,
              CQL_FILTER: data.filter || '',
              TILED: true
            },
            serverType: 'geoserver',
            crossOrigin: 'anonymous',
            transition: 0,
            tileLoadFunction: function (image, src) {
              // 创建一个HTTP请求来获取图像
              const xhr = new XMLHttpRequest();
              xhr.open('GET', src);
              xhr.responseType = 'blob'; // 期望的响应类型是blob
              // 设置Authorization请求头
              xhr.setRequestHeader('Authorization', 'Basic ' + window.btoa('data_services:data_services'));

              // 请求加载成功时的处理
              xhr.onload = function () {
                if (this.status === 200) {
                  const blob = this.response;
                  // 创建一个对象URL
                  const objectUrl = URL.createObjectURL(blob);
                  image.getImage().src = objectUrl;
                }
              };

              // 发送请求
              xhr.send();
            }
          });

          this.assessLayer = new TileLayer({
            lid: 'wgu-assess-layer',
            displayInLayerList: false,
            source,
            visible: true,
            zIndex: 70
          });

          this.map.addLayer(this.assessLayer);
        }
      } catch (error) {
        console.error('Failed to fetch prelAssess data:', error);
        // this.$notify({
        //   title: 'Error',
        //   text: '获取预评估数据失败，请稍后重试'
        // });
      }
    }
  }
}
</script>
